import { createContext, useContext } from 'react';

// Drawer control, handed DOWNWARD from App to the assistant subtree.
//
// WHY THIS EXISTS. Components below App used to ask for the drawer by posting
// `MAA_OPEN_DRAWER` onto the shared assistant BroadcastChannel, which the drawer
// controller (in App, above them) listened for. That is a descendant→ancestor call routed
// through the browser's cross-tab bus — scar tissue from the removed loader↔component
// package boundary, where a direct call genuinely was not possible. It is now: App owns the
// drawer state machine and everything that wants to open the drawer is beneath it.
//
// The direction matters. This context only ever flows down, so it carries none of the
// topology hazard that sank the WS-10.2 Shell store or forced the LiveAgent port: App is
// strictly above both the orchestrator and the journey content.
//
// ⚠ NOT a replacement for the cross-tab hop. Opening the drawer in OTHER tabs is deliberate
// behavior (user-confirmed 2026-07-27) and stays on the BroadcastChannel — but it is now
// emitted BY the drawer controller, which owns drawer state, rather than by whichever
// component happened to want the drawer. See useDrawerController.openDrawer.
//
// DEFAULT IS INERT, NOT A THROW — a deliberate divergence from `useJourney` (which throws
// outside its provider, see journeys/context.js). Rendering a message without a journey is
// meaningless, so failing fast there is right. Here the orchestrator legitimately mounts
// standalone in unit tests with no App above it, and "there is no drawer to control" is a
// coherent state for a callback-time capability. Wiring is pinned by a test that asserts the
// real provider supplies real handlers, rather than by a runtime throw that would force
// every isolated orchestrator mount to grow a wrapper.
const NOOP_DRAWER_CONTROL = Object.freeze({
  openDrawer: () => {},
});

export const DrawerControlContext = createContext(NOOP_DRAWER_CONTROL);

/**
 * Drawer commands available to the assistant subtree.
 * @returns {{ openDrawer: () => void }}
 */
export const useDrawerControl = () => useContext(DrawerControlContext);
